import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Trash2,
  Key,
  Eye,
  EyeOff,
  Save,
  CheckCircle2,
  Cpu,
  Webhook,
  RefreshCw,
  Copy,
  AlertTriangle,
  Mail,
  Unlink,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export function SettingsIntegrationsPage() {
  const list = trpc.settings.listApiKeys.useQuery();
  const items = list.data ?? [];

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6">
      <header className="mb-6">
        <h2 className="text-lg font-semibold">Integrations</h2>
        <p className="text-sm text-muted-foreground">
          Configure API keys for additional data sources. Values are stored in the database
          and take priority over environment variables.
        </p>
      </header>

      <div className="mb-3">
        <GmailIntegrationPanel />
      </div>

      {list.isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <ApiKeyRow key={item.provider} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function GmailIntegrationPanel() {
  const utils = trpc.useUtils();
  const status = trpc.gmail.status.useQuery();
  const disconnect = trpc.gmail.disconnect.useMutation({
    onSuccess: () => {
      toast.success("Gmail account disconnected");
      utils.gmail.status.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to disconnect"),
  });
  const setPrimary = trpc.gmail.setPrimary.useMutation({
    onSuccess: () => {
      toast.success("Sending account updated");
      utils.gmail.status.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to update"),
  });
  const pollNow = trpc.gmail.pollNow.useMutation({
    onSuccess: (res) => {
      const perAccount = res.accounts
        .map((a) => `${a.mailbox}: ${a.fetched} fetched, ${a.matched} matched`)
        .join(" · ");
      toast.success(
        `Polled ${res.accounts.length} account${res.accounts.length === 1 ? "" : "s"} — ${perAccount || "nothing to fetch"}`
      );
      utils.gmail.status.invalidate();
    },
    onError: (err) => toast.error(err.message || "Poll failed"),
  });

  const data = status.data;
  const accounts = data?.accounts ?? [];

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Gmail (email)</span>
            {data?.connected ? (
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                {accounts.length} connected
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-zinc-100 text-zinc-600">
                not connected
              </Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Every connected inbox is polled every 5 minutes for unread messages.
            Replies from a sender whose email matches an opportunity contact are appended
            as a note and the opportunity is tagged "Client responded". All outbound
            email (proposals, replies, follow-ups) is sent from the account marked
            "sending".
          </p>
          {data && !data.googleClientConfigured && (
            <p className="mt-1 text-xs text-amber-700">
              GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET must be set in the
              environment before connecting.
            </p>
          )}
        </div>
      </div>

      {accounts.length > 0 && (
        <div className="mb-3 space-y-2">
          {accounts.map((account) => (
            <div
              key={account.id}
              className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <code className="rounded bg-muted px-1 py-0.5 text-xs">
                    {account.email}
                  </code>
                  {account.isPrimary && (
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                      sending
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {account.lastPolledAt
                    ? `Last synced: ${new Date(account.lastPolledAt).toLocaleString()}`
                    : "Not synced yet"}
                </p>
              </div>
              {!account.isPrimary && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setPrimary.mutate({ accountId: account.id })}
                  disabled={setPrimary.isPending}
                >
                  Use for sending
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => disconnect.mutate({ accountId: account.id })}
                disabled={disconnect.isPending}
                className="text-muted-foreground hover:text-destructive"
              >
                <Unlink className="mr-1 h-3.5 w-3.5" />
                Disconnect
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          asChild
          disabled={!data?.googleClientConfigured}
        >
          <a href={data?.connectUrl ?? "/auth/gmail/connect"}>
            <Mail className="mr-1 h-3.5 w-3.5" />
            {accounts.length > 0 ? "Connect another account" : "Connect Gmail"}
          </a>
        </Button>
        {data?.connected && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => pollNow.mutate()}
            disabled={pollNow.isPending}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Poll now
          </Button>
        )}
      </div>
    </div>
  );
}

type ApiKeyItem = NonNullable<
  ReturnType<typeof trpc.settings.listApiKeys.useQuery>["data"]
>[number];

function ApiKeyRow({ item }: { item: ApiKeyItem }) {
  const utils = trpc.useUtils();
  const [value, setValue] = useState("");
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    setValue("");
    setReveal(false);
  }, [item.masked, item.source]);

  const save = trpc.settings.setApiKey.useMutation({
    onSuccess: () => {
      toast.success(value.trim() ? "Key saved" : "Key removed");
      setValue("");
      setReveal(false);
      utils.settings.listApiKeys.invalidate();
      utils.contacts.availableEnrichmentProviders.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to save"),
  });

  const handleSave = () => {
    save.mutate({ provider: item.provider, value: value.trim() });
  };

  const handleClear = () => {
    if (!item.configured || item.source !== "db") return;
    save.mutate({ provider: item.provider, value: "" });
  };

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Key className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{item.label}</span>
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              {item.envName}
            </code>
            <StatusBadge item={item} />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{item.help}</p>
          {item.configured && item.masked && (
            <p className="mt-1 text-xs text-muted-foreground">
              Current: <code className="rounded bg-muted px-1 py-0.5">{item.masked}</code>
              {item.source === "env" && (
                <span className="ml-1 text-amber-700">
                  (from .env — entering a value here will take priority)
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[260px] flex-1">
          <Input
            type={reveal ? "text" : "password"}
            placeholder={item.configured ? "Change key…" : "Enter key…"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="pr-10 font-mono text-xs"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={() => setReveal((x) => !x)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={reveal ? "Hide" : "Show"}
          >
            {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={save.isPending || value.trim().length === 0}
        >
          <Save className="mr-1 h-3.5 w-3.5" />
          Save
        </Button>
        {item.source === "db" && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleClear}
            disabled={save.isPending}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Remove
          </Button>
        )}
      </div>

      {item.provider === "anthropic" && item.configured && <AnthropicModelPicker />}
      {item.provider === "brevo" && item.configured && <BrevoSenderPanel />}
      {item.provider === "brevo" && item.configured && <BrevoWebhooksPanel />}
      {item.provider === "fibery" && item.configured && <FiberySyncSubPanel />}
    </div>
  );
}

const EVENT_LABELS: Record<string, string> = {
  sent: "Sent",
  request: "Request",
  delivered: "Delivered",
  softBounce: "Soft bounce",
  hardBounce: "Hard bounce",
  blocked: "Blocked",
  spam: "Spam",
  invalid: "Invalid email",
  deferred: "Deferred",
  click: "Click",
  opened: "Opened",
  uniqueOpened: "Unique opened",
  unsubscribed: "Unsubscribed",
  error: "Error",
  loadedByProxy: "Loaded by proxy",
};

function BrevoSenderPanel() {
  const utils = trpc.useUtils();
  const info = trpc.settings.getBrevoSender.useQuery();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  // Seed the inputs from the resolved values once loaded (or when they change
  // after a save), so the admin edits the current sender rather than a blank.
  useEffect(() => {
    if (info.data) {
      setEmail(info.data.email);
      setName(info.data.name);
    }
  }, [info.data?.email, info.data?.name]);

  const save = trpc.settings.setBrevoSender.useMutation({
    onSuccess: () => {
      toast.success("Sender saved");
      utils.settings.getBrevoSender.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to save sender"),
  });

  const data = info.data;

  if (info.isLoading || !data) {
    return (
      <div className="mt-4 border-t pt-4 text-xs text-muted-foreground">
        Loading sender settings…
      </div>
    );
  }

  const handleSave = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error("Sender email is required");
      return;
    }
    save.mutate({ email: trimmedEmail, name: name.trim() || null });
  };

  const handleReset = () => {
    // Clearing the DB rows falls back to the .env defaults.
    save.mutate({ email: null, name: null });
  };

  const dirty =
    email.trim() !== data.email || name.trim() !== data.name;
  const usingDb = data.emailSource === "db" || data.nameSource === "db";

  return (
    <div className="mt-4 space-y-3 border-t pt-4">
      <div className="flex flex-wrap items-center gap-2">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Sending email</span>
        <Badge
          variant="secondary"
          className={cn(
            "gap-1 text-[10px]",
            usingDb
              ? "bg-emerald-100 text-emerald-700"
              : "bg-blue-100 text-blue-700"
          )}
        >
          {usingDb ? "from DB" : "from .env"}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        Only the "from" name is used on outgoing emails — the address is always
        the connected Gmail mailbox above. The email field remains only for the
        legacy Brevo webhook history.
      </p>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            From email
          </label>
          <Input
            type="email"
            placeholder={data.envEmail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="text-sm"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            From name
          </label>
          <Input
            type="text"
            placeholder={data.envName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="text-sm"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={save.isPending || !dirty}
        >
          <Save className="mr-1 h-3.5 w-3.5" />
          Save
        </Button>
        {usingDb && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleReset}
            disabled={save.isPending}
            className="text-muted-foreground hover:text-destructive"
          >
            Reset to .env default
          </Button>
        )}
      </div>
    </div>
  );
}

function BrevoWebhooksPanel() {
  const utils = trpc.useUtils();
  const config = trpc.brevoWebhooks.config.useQuery();
  const list = trpc.brevoWebhooks.list.useQuery(undefined, {
    enabled: config.data?.brevoConfigured ?? false,
    retry: false,
  });

  const allEvents = config.data?.availableEvents ?? [];
  const expectedUrl = config.data?.expectedUrl ?? "";
  const hasSecret = config.data?.hasSecret ?? false;

  const ours = useMemo(
    () => (list.data ?? []).find((h) => h.pointsAtUs),
    [list.data]
  );
  const others = useMemo(
    () => (list.data ?? []).filter((h) => !h.pointsAtUs),
    [list.data]
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (ours) {
      // Brevo sometimes returns legacy event names (e.g. `uniqueProxyOpen`)
      // that are no longer in its writable enum — drop them so saving the
      // form doesn't fail validation on round-trip.
      const known = new Set(allEvents);
      setSelected(new Set(ours.events.filter((e) => known.has(e))));
    } else if (allEvents.length > 0) {
      setSelected(new Set(allEvents));
    }
  }, [ours, allEvents]);

  const register = trpc.brevoWebhooks.register.useMutation({
    onSuccess: (res) => {
      toast.success(
        res.action === "created" ? "Webhook created in Brevo" : "Webhook updated"
      );
      utils.brevoWebhooks.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const removeHook = trpc.brevoWebhooks.delete.useMutation({
    onSuccess: () => {
      toast.success("Webhook deleted");
      utils.brevoWebhooks.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const rotate = trpc.brevoWebhooks.rotateSecret.useMutation({
    onSuccess: () => {
      toast.success("Secret rotated — re-register the webhook to apply");
      utils.brevoWebhooks.config.invalidate();
      utils.brevoWebhooks.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const clearSecret = trpc.brevoWebhooks.clearSecret.useMutation({
    onSuccess: () => {
      toast.success("Secret cleared");
      utils.brevoWebhooks.config.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const toggleEvent = (e: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(allEvents));
  const selectNone = () => setSelected(new Set());

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(expectedUrl);
      toast.success("URL copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleSave = () => {
    if (selected.size === 0) {
      toast.error("Select at least one event");
      return;
    }
    register.mutate({ events: Array.from(selected) as never });
  };

  const handleEnableAll = () => {
    register.mutate({ all: true });
  };

  return (
    <div className="mt-4 space-y-3 border-t pt-4">
      <div className="flex flex-wrap items-center gap-2">
        <Webhook className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Webhooks</span>
        <span className="text-xs text-muted-foreground">
          Receive open/click/delivery events from Brevo into the opportunity history.
        </span>
      </div>

      <div className="rounded-md border bg-muted/30 p-3">
        <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
          Endpoint URL (Brevo will POST events here)
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <code className="flex-1 break-all rounded bg-background px-2 py-1 font-mono text-xs">
            {expectedUrl || "—"}
          </code>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={copyUrl}
            disabled={!expectedUrl}
          >
            <Copy className="mr-1 h-3.5 w-3.5" />
            Copy
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {hasSecret ? (
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="mr-1 h-3 w-3" />
              Secret active
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
              <AlertTriangle className="mr-1 h-3 w-3" />
              No secret — endpoint is public
            </Badge>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => rotate.mutate({})}
            disabled={rotate.isPending}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            {hasSecret ? "Rotate secret" : "Generate secret"}
          </Button>
          {hasSecret && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => clearSecret.mutate()}
              disabled={clearSecret.isPending}
              className="text-muted-foreground hover:text-destructive"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            Events ({selected.size} of {allEvents.length} selected)
          </span>
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={selectAll}
            >
              All
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={selectNone}
            >
              None
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {allEvents.map((e) => {
            const active = selected.has(e);
            return (
              <button
                key={e}
                type="button"
                onClick={() => toggleEvent(e)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs transition-colors",
                  active
                    ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                    : "border-zinc-200 bg-background text-muted-foreground hover:bg-muted"
                )}
              >
                {EVENT_LABELS[e] ?? e}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={register.isPending}
        >
          <Save className="mr-1 h-3.5 w-3.5" />
          {ours ? "Update in Brevo" : "Register in Brevo"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleEnableAll}
          disabled={register.isPending}
        >
          Enable all events
        </Button>
        {ours && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => removeHook.mutate({ id: ours.id })}
            disabled={removeHook.isPending}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Delete from Brevo
          </Button>
        )}
      </div>

      {list.isLoading && (
        <p className="text-xs text-muted-foreground">Loading webhooks from Brevo…</p>
      )}
      {list.error && (
        <p className="text-xs text-destructive">{list.error.message}</p>
      )}

      {ours && (
        <div className="rounded-md border p-2 text-xs">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="mr-1 h-3 w-3" />
              Registered
            </Badge>
            <code className="font-mono">id: {ours.id}</code>
            {!ours.hasAllEvents && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                Missing some events
              </Badge>
            )}
            {hasSecret && !ours.hasSecret && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                Out-of-date secret
              </Badge>
            )}
          </div>
          <div className="break-all font-mono text-muted-foreground">{ours.url}</div>
          <div className="mt-1 text-muted-foreground">
            Subscribed: {ours.events.join(", ")}
          </div>
        </div>
      )}

      {others.length > 0 && (
        <details className="rounded-md border p-2 text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            Other webhooks in this Brevo account ({others.length})
          </summary>
          <div className="mt-2 space-y-2">
            {others.map((h) => (
              <div key={h.id} className="rounded border p-2">
                <div className="break-all font-mono">{h.url}</div>
                <div className="mt-1 text-muted-foreground">
                  id: {h.id} · {h.events.length} events
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function FiberySyncSubPanel() {
  const utils = trpc.useUtils();
  const status = trpc.fibery.status.useQuery();
  const syncNow = trpc.fibery.syncNow.useMutation({
    onSuccess: (res) => {
      toast.success(
        `Sync done: ${res.clientsCreated} clients created, ${res.clientsUpdated} updated, ${res.contactsCreated} contacts created, ${res.contactsUpdated} updated${res.errors > 0 ? `, ${res.errors} errors` : ""}`
      );
      utils.fibery.status.invalidate();
    },
    onError: (err) => toast.error(err.message || "Sync failed"),
  });

  const lastResult = status.data?.lastSyncResult;
  const errorMessages = lastResult?.errorMessages ?? [];

  return (
    <div className="mt-3 space-y-2 border-t pt-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => syncNow.mutate()}
          disabled={syncNow.isPending || !status.data?.configured}
        >
          <RefreshCw className="mr-1 h-3.5 w-3.5" />
          {syncNow.isPending ? "Syncing…" : "Sync now"}
        </Button>
        {status.data?.lastSyncAt && (
          <span className="text-xs text-muted-foreground">
            Last sync: {new Date(status.data.lastSyncAt).toLocaleString()}
          </span>
        )}
        {lastResult && (
          <span className="text-xs text-muted-foreground">
            clients +{lastResult.clientsCreated} ~{lastResult.clientsUpdated} ={lastResult.clientsSkipped}
            {" · "}contacts +{lastResult.contactsCreated} ~{lastResult.contactsUpdated}
            {lastResult.errors > 0 && (
              <span className="ml-1 font-medium text-destructive">· {lastResult.errors} errors</span>
            )}
          </span>
        )}
        {!status.data?.hasAccount && (
          <span className="text-xs text-amber-700">
            Set <code className="rounded bg-muted px-1 py-0.5">FIBERY_ACCOUNT</code> env var to enable
          </span>
        )}
      </div>
      {errorMessages.length > 0 && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2">
          <p className="mb-1 text-xs font-medium text-destructive">Last sync errors:</p>
          <ul className="space-y-0.5">
            {errorMessages.map((msg, i) => (
              <li key={i} className="font-mono text-xs text-destructive break-all">
                {msg}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AnthropicModelPicker() {
  const utils = trpc.useUtils();
  const info = trpc.settings.getAnthropicModel.useQuery();
  const save = trpc.settings.setAnthropicModel.useMutation({
    onSuccess: () => {
      toast.success("Model saved");
      utils.settings.getAnthropicModel.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to save model"),
  });

  if (info.isLoading || !info.data) {
    return (
      <div className="mt-3 border-t pt-3 text-xs text-muted-foreground">
        Loading model settings…
      </div>
    );
  }

  const data = info.data;
  const selectedValue = data.dbValue ?? "__env__";

  const handleChange = (value: string) => {
    if (value === "__env__") {
      save.mutate({ model: null });
    } else {
      save.mutate({ model: value });
    }
  };

  return (
    <div className="mt-3 border-t pt-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Cpu className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Model</span>
        <Badge
          variant="secondary"
          className={cn(
            "gap-1 text-[10px]",
            data.source === "db"
              ? "bg-emerald-100 text-emerald-700"
              : "bg-blue-100 text-blue-700"
          )}
        >
          {data.source === "db" ? "from DB" : "from .env"}
        </Badge>
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          {data.current}
        </code>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        Choose the Claude model used for all Anthropic calls (briefs, proposals, agent, enrichment).
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={selectedValue}
          onValueChange={handleChange}
          disabled={save.isPending}
        >
          <SelectTrigger className="h-9 min-w-[280px] flex-1">
            <SelectValue placeholder="Select model…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__env__">
              Default (.env: {data.envValue})
            </SelectItem>
            {data.options.map((opt) => (
              <SelectItem key={opt.id} value={opt.id}>
                <div className="flex flex-col">
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {opt.description}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function StatusBadge({ item }: { item: ApiKeyItem }) {
  if (!item.configured) {
    return (
      <Badge variant="secondary" className="bg-zinc-100 text-zinc-600">
        not configured
      </Badge>
    );
  }
  return (
    <Badge
      variant="secondary"
      className={cn(
        "gap-1",
        item.source === "db"
          ? "bg-emerald-100 text-emerald-700"
          : "bg-blue-100 text-blue-700"
      )}
    >
      <CheckCircle2 className="h-3 w-3" />
      {item.source === "db" ? "from DB" : "from .env"}
    </Badge>
  );
}
